// FILE: src/pages/Payroll/PayrollPage.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { payrollAPI, caregiversAPI } from '../../api/services';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import '../../components/UI/UI.css';
import './Payroll.css';

const fmtMoney = (n) => `$${parseFloat(n||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}`;
const fmtDate  = (d) => d ? new Date(d).toLocaleDateString('en-US',{year:'numeric',month:'short',day:'numeric'}) : '—';

const STATUS_STYLES = { 'Pending':'badge-warning', 'Processed':'badge-info', 'Paid':'badge-success' };

export default function PayrollPage() {
  const { hasRole } = useAuth();
  const canEdit     = hasRole('Admin','Billing');

  const [records,    setRecords]    = useState([]);
  const [filtered,   setFiltered]   = useState([]);
  const [caregivers, setCaregivers] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [showCalc,   setShowCalc]   = useState(false);
  const [showModal,  setShowModal]  = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [deleting,   setDeleting]   = useState(null);
  const [delLoad,    setDelLoad]    = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [pRes, cgRes] = await Promise.all([payrollAPI.getAll(), caregiversAPI.getAll()]);
      setRecords(pRes.data.data   || []);
      setFiltered(pRes.data.data  || []);
      setCaregivers(cgRes.data.data || []);
    } catch { toast.error('Failed to load payroll.'); }
    finally   { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!search.trim()) { setFiltered(records); return; }
    const q = search.toLowerCase();
    setFiltered(records.filter(r => (r.CaregiverName||'').toLowerCase().includes(q)));
  }, [search, records]);

  const handleDelete = async () => {
    if (!deleting) return;
    setDelLoad(true);
    try {
      await payrollAPI.delete(deleting.ID);
      toast.success('Payroll record deleted.');
      setDeleting(null); load();
    } catch { toast.error('Delete failed.'); }
    finally   { setDelLoad(false); }
  };

  const totalPaid    = records.filter(r=>r.PayrollStatus==='Paid').reduce((s,r)=>s+parseFloat(r.NetPay||0),0);
  const totalPending = records.filter(r=>r.PayrollStatus!=='Paid').reduce((s,r)=>s+parseFloat(r.NetPay||0),0);

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h1>Payroll</h1>
          <p>{records.length} payroll records</p>
        </div>
        <div style={{display:'flex',gap:10}}>
          <div className="search-bar">
            <span className="search-icon">🔍</span>
            <input className="form-control" placeholder="Search caregiver…"
              value={search} onChange={e=>setSearch(e.target.value)}
              style={{paddingLeft:36,minWidth:240}}/>
          </div>
          {canEdit && (
            <>
              <button className="btn btn-secondary" onClick={() => setShowCalc(true)}>🧮 Auto-Calculate</button>
              <button className="btn btn-primary"   onClick={() => { setEditing(null); setShowModal(true); }}>➕ Add Record</button>
            </>
          )}
        </div>
      </div>

      <div className="stats-grid-4" style={{marginBottom:20}}>
        {[
          {label:'Total Records',   value:records.length,                                         icon:'📋'},
          {label:'Total Paid',      value:fmtMoney(totalPaid),                                    icon:'✅'},
          {label:'Pending Payment', value:fmtMoney(totalPending),                                 icon:'⏳'},
          {label:'Caregivers Paid', value:records.filter(r=>r.PayrollStatus==='Paid').length,      icon:'👨‍⚕️'},
        ].map(s=>(
          <div className="stat-card" key={s.label}>
            <div className="stat-card-header"><span className="stat-card-label">{s.label}</span><span style={{fontSize:20}}>{s.icon}</span></div>
            <div className="stat-card-value">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="card" style={{padding:0}}>
        {loading ? (
          <div className="loading-state">⏳ Loading payroll…</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">💵</div>
            <div className="empty-state-text">{search ? 'No records match.' : 'No payroll records yet.'}</div>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="ec-table">
              <thead>
                <tr><th>Caregiver</th><th>Pay Period</th><th>Hours</th><th>Gross Pay</th><th>Deductions</th><th>Net Pay</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {filtered.map(r=>(
                  <tr key={r.ID}>
                    <td className="bold">{r.CaregiverName||'—'}</td>
                    <td style={{fontSize:'0.8rem',color:'var(--gray-500)'}}>
                      {fmtDate(r.PayPeriodStart)} – {fmtDate(r.PayPeriodEnd)}
                    </td>
                    <td>{r.TotalHours ? `${parseFloat(r.TotalHours).toFixed(2)}h` : '—'}</td>
                    <td>{fmtMoney(r.GrossPay)}</td>
                    <td style={{color:'var(--danger)'}}>{r.Deductions ? fmtMoney(r.Deductions) : '—'}</td>
                    <td style={{fontWeight:700,color:'var(--success)'}}>{fmtMoney(r.NetPay)}</td>
                    <td><span className={`badge ${STATUS_STYLES[r.PayrollStatus]||'badge-gray'}`}>{r.PayrollStatus||'Pending'}</span></td>
                    <td>
                      <div style={{display:'flex',gap:6}}>
                        {canEdit && (
                          <>
                            <button className="btn btn-sm btn-secondary" onClick={() => { setEditing(r); setShowModal(true); }}>✏️</button>
                            <button className="btn btn-sm btn-danger"    onClick={() => setDeleting(r)}>🗑️</button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <PayrollFormModal record={editing} caregivers={caregivers}
          onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); load(); }} />
      )}
      {showCalc && (
        <CalcModal caregivers={caregivers}
          onClose={() => setShowCalc(false)} onSaved={() => { setShowCalc(false); load(); }} />
      )}
      {deleting && (
        <div className="modal-overlay">
          <div className="modal modal-sm">
            <div className="modal-header"><span className="modal-title">🗑️ Delete Record</span><button className="modal-close" onClick={() => setDeleting(null)}>✕</button></div>
            <div className="modal-body"><p style={{color:'var(--gray-600)'}}>Delete payroll record for <strong>{deleting.CaregiverName}</strong>?</p></div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeleting(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete} disabled={delLoad}>{delLoad?'Deleting…':'Yes, Delete'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PayrollFormModal({ record, caregivers, onClose, onSaved }) {
  const isEdit = !!record;
  const [form,   setForm]   = useState({
    CaregiverID:    record?.CaregiverID   || '',
    PayPeriodStart: record?.PayPeriodStart ? record.PayPeriodStart.split('T')[0] : '',
    PayPeriodEnd:   record?.PayPeriodEnd   ? record.PayPeriodEnd.split('T')[0]   : '',
    TotalHours:     record?.TotalHours     || '',
    HourlyRate:     record?.HourlyRate     || '',
    GrossPay:       record?.GrossPay       || '',
    Deductions:     record?.Deductions     || '',
    NetPay:         record?.NetPay         || '',
    PayrollStatus:  record?.PayrollStatus  || 'Pending',
  });
  const [saving, setSaving] = useState(false);

  const set = (f,v) => setForm(p => {
    const u = {...p,[f]:v};
    if (f==='TotalHours'||f==='HourlyRate') {
      const h=parseFloat(f==='TotalHours'?v:p.TotalHours)||0;
      const r=parseFloat(f==='HourlyRate'?v:p.HourlyRate)||0;
      const gross=h*r; u.GrossPay=gross.toFixed(2);
      const ded=parseFloat(p.Deductions)||0;
      u.NetPay=(gross-ded).toFixed(2);
    }
    if (f==='Deductions') {
      const gross=parseFloat(p.GrossPay)||0;
      u.NetPay=(gross-parseFloat(v||0)).toFixed(2);
    }
    return u;
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isEdit) { await payrollAPI.update(record.ID,form); toast.success('Updated.'); }
      else        { await payrollAPI.create(form);           toast.success('Record created.'); }
      onSaved();
    } catch (err) { toast.error(err.response?.data?.message||'Save failed.'); }
    finally        { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <span className="modal-title">{isEdit?'✏️ Edit Payroll':'➕ Add Payroll Record'}</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Caregiver</label>
              <select className="form-control" value={form.CaregiverID} onChange={e=>set('CaregiverID',e.target.value)}>
                <option value="">— Select —</option>
                {caregivers.map(c=><option key={c.UserID} value={c.UserID}>{c.FirstName} {c.LastName}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-control" value={form.PayrollStatus} onChange={e=>set('PayrollStatus',e.target.value)}>
                {['Pending','Processed','Paid'].map(s=><option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Pay Period Start</label>
              <input className="form-control" type="date" value={form.PayPeriodStart} onChange={e=>set('PayPeriodStart',e.target.value)}/>
            </div>
            <div className="form-group">
              <label className="form-label">Pay Period End</label>
              <input className="form-control" type="date" value={form.PayPeriodEnd} onChange={e=>set('PayPeriodEnd',e.target.value)}/>
            </div>
          </div>
          <div className="form-row-3">
            <div className="form-group">
              <label className="form-label">Total Hours</label>
              <input className="form-control" type="number" step="0.25" value={form.TotalHours} onChange={e=>set('TotalHours',e.target.value)} placeholder="0.00"/>
            </div>
            <div className="form-group">
              <label className="form-label">Hourly Rate ($)</label>
              <input className="form-control" type="number" step="0.01" value={form.HourlyRate} onChange={e=>set('HourlyRate',e.target.value)} placeholder="0.00"/>
            </div>
            <div className="form-group">
              <label className="form-label">Gross Pay ($)</label>
              <input className="form-control" type="number" step="0.01" value={form.GrossPay} onChange={e=>set('GrossPay',e.target.value)} placeholder="0.00"/>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Deductions ($)</label>
              <input className="form-control" type="number" step="0.01" value={form.Deductions} onChange={e=>set('Deductions',e.target.value)} placeholder="0.00"/>
            </div>
            <div className="form-group">
              <label className="form-label">Net Pay ($)</label>
              <input className="form-control" type="number" step="0.01" value={form.NetPay} onChange={e=>set('NetPay',e.target.value)} placeholder="0.00" style={{fontWeight:700}}/>
            </div>
          </div>
          {form.GrossPay && (
            <div style={{background:'var(--success-pale)',border:'1px solid #a7f3d0',borderRadius:'var(--radius-md)',padding:'10px 14px',display:'flex',justifyContent:'space-between'}}>
              <span style={{fontSize:'0.85rem',color:'#065f46',fontWeight:600}}>
                💵 Gross: {fmtMoney(form.GrossPay)}  −  Deductions: {fmtMoney(form.Deductions)}
              </span>
              <span style={{fontWeight:700,color:'#065f46'}}>Net: {fmtMoney(form.NetPay)}</span>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving?'Saving…':isEdit?'💾 Update':'✅ Add Record'}</button>
        </div>
      </div>
    </div>
  );
}

function CalcModal({ caregivers, onClose, onSaved }) {
  const [form,   setForm]   = useState({ CaregiverID:'', PayPeriodStart:'', PayPeriodEnd:'' });
  const [saving, setSaving] = useState(false);

  const handleCalc = async () => {
    if (!form.CaregiverID||!form.PayPeriodStart||!form.PayPeriodEnd) {
      toast.error('Please fill all fields.'); return;
    }
    setSaving(true);
    try {
      const res = await payrollAPI.calculate(form);
      toast.success(`Payroll calculated: ${res.data.data?.NetPay ? '$'+parseFloat(res.data.data.NetPay).toFixed(2) : 'Done'}`);
      onSaved();
    } catch (err) { toast.error(err.response?.data?.message||'Calculation failed.'); }
    finally        { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-sm">
        <div className="modal-header">
          <span className="modal-title">🧮 Auto-Calculate Payroll</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <p style={{color:'var(--gray-500)',fontSize:'0.85rem',marginBottom:16}}>
            Automatically calculates pay from attendance records for the selected period.
          </p>
          <div className="form-group">
            <label className="form-label">Caregiver</label>
            <select className="form-control" value={form.CaregiverID} onChange={e=>setForm(p=>({...p,CaregiverID:e.target.value}))}>
              <option value="">— Select —</option>
              {caregivers.map(c=><option key={c.UserID} value={c.UserID}>{c.FirstName} {c.LastName}</option>)}
            </select>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Period Start</label>
              <input className="form-control" type="date" value={form.PayPeriodStart} onChange={e=>setForm(p=>({...p,PayPeriodStart:e.target.value}))}/>
            </div>
            <div className="form-group">
              <label className="form-label">Period End</label>
              <input className="form-control" type="date" value={form.PayPeriodEnd} onChange={e=>setForm(p=>({...p,PayPeriodEnd:e.target.value}))}/>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleCalc} disabled={saving}>{saving?'Calculating…':'🧮 Calculate'}</button>
        </div>
      </div>
    </div>
  );
}